import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useCacheContext, CacheKeys } from "../Cache";
import {
  switchDisabilityState,
  switchUpdatingState,
  getEnvsDefaultKeyBooleanState,
} from "./TranslatiorUpdater.helpers";
import { Api } from "../../api";
import { ENVIRONMENTS_FILTERS } from "../../helpers/EnvironmentsFilters";
import { IEnvironment, EnvType } from "../../interfaces";

interface State {
  envs: IEnvironment[];
  internalEnvs: IEnvironment[];
  currentPickedEnv: IEnvironment;
  updatingState: Record<string, boolean>;
  disableState: Record<string, boolean>;
  isProdUpdateConfirmationModalOpen: boolean;
}

const DEFAULT_ENVIRONMENT: IEnvironment = {
  name: "",
  uat: "",
  prod: "",
  uatDate: "",
  prodDate: "",
  rm: "",
  administration: "",
  isoCode: "",
  priority: 0,
  isLive: false,
};

const DEFAULT_STATE: State = {
  envs: [],
  internalEnvs: [],
  currentPickedEnv: DEFAULT_ENVIRONMENT,
  updatingState: {},
  disableState: {},
  isProdUpdateConfirmationModalOpen: false,
};

const DEFAULT_FILTER = ENVIRONMENTS_FILTERS?.stringFilter("name");

const getEnvSupportedLangugages = async (envUrl: string) => {
  const envSettings = await Api.getEnvSupportedLanguages(envUrl);
  const envSupportedLanguages =
    envSettings?.data?.localization?.languages?.supported;

  return envSupportedLanguages;
};

const updateEnvTranslations = async (
  env: IEnvironment,
  envType: EnvType,
  onRequestEnd: () => void
) => {
  const envName = env?.name;
  const envUrl =
    envType === EnvType.prod ? env?.prod : ((env?.uat || env?.url) as string);

  try {
    const envSupportedLanguages = await getEnvSupportedLangugages(envUrl);
    await Api.updateEnvTranslations(
      envSupportedLanguages,
      env?.isoCode,
      envType
    );

    toast.success(
      `${envName.toUpperCase()} ${envType.toUpperCase()} translations updated!`
    );
    onRequestEnd();
  } catch (e) {
    toast.error(
      `Error occured during ${envName.toUpperCase()} ${envType.toUpperCase()} translations update!`
    );
    onRequestEnd();
  }
};

export const useTranslationUpdater = () => {
  const [state, setState] = useState(DEFAULT_STATE);
  const { getFromCache } = useCacheContext();

  useEffect(() => {
    const envs = getFromCache(CacheKeys.environments)?.sort(DEFAULT_FILTER);

    const internalEnvs = getFromCache(CacheKeys.internalEnvironments);

    setState({
      envs,
      internalEnvs,
      currentPickedEnv: DEFAULT_ENVIRONMENT,
      updatingState: getEnvsDefaultKeyBooleanState([...envs, ...internalEnvs]),
      disableState: getEnvsDefaultKeyBooleanState([...envs, ...internalEnvs]),
      isProdUpdateConfirmationModalOpen: false,
    });
  }, [getFromCache]);

  const onUpdateEnv = async (env: IEnvironment, envType: EnvType) => {
    setState((prevState) => ({
      ...prevState,
      isProdUpdateConfirmationModalOpen: false,
      updatingState: switchUpdatingState(state.updatingState, env?.name),
      disableState: switchDisabilityState(state.disableState, env?.name),
    }));

    const onUpdateEnd = () => {
      setState((prevState) => ({
        ...prevState,
        isProdUpdateConfirmationModalOpen: false,
        currentPickedEnv: DEFAULT_ENVIRONMENT,
        updatingState: getEnvsDefaultKeyBooleanState(state?.envs),
        disableState: getEnvsDefaultKeyBooleanState(state?.envs),
      }));
    };

    await updateEnvTranslations(env, envType, onUpdateEnd);
  };

  const onUpdateProdModalClose = () => {
    setState((prevState) => ({
      ...prevState,
      currentPickedEnv: DEFAULT_ENVIRONMENT,
      isProdUpdateConfirmationModalOpen: false,
    }));
  };

  const onUpdateProdEnvConfirm = () =>
    onUpdateEnv(state?.currentPickedEnv, EnvType.prod);

  const onUpdateProdEnvClick = (env: IEnvironment) => {
    setState((prevState) => ({
      ...prevState,
      currentPickedEnv: env,
      isProdUpdateConfirmationModalOpen: true,
    }));
  };

  const onUpdateUatEnvClick = (env: IEnvironment) =>
    onUpdateEnv(env, EnvType.uat);

  const onUpdateInternalEnvClick = (env: IEnvironment) =>
    onUpdateEnv(env, EnvType.internal);

  return {
    state,
    onUpdateUatEnvClick,
    onUpdateProdEnvClick,
    onUpdateProdEnvConfirm,
    onUpdateProdModalClose,
    onUpdateInternalEnvClick,
  } as const;
};
